/* Copyright (c) 2026, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

/**
 * Web ↔ RN Vega bridge protocol types.
 * Keep in sync with client/tv/vega/gfn/src/services/bridge/types/protocol.ts
 */

export enum VegaBridgeCategory {
    WindowControl = 'WindowControl',
    Iap = 'Iap',
    Storage = 'Storage',
    SystemInfo = 'SystemInfo',
    ApplicationInfo = 'ApplicationInfo',
    Logging = 'Logging'
}

export enum VegaBridgeMethod {
    AppExit = 'AppExit',
    StoreData = 'StoreData',
    GetUserSpecifiedDeviceName = 'GetUserSpecifiedDeviceName',
    SetWebViewUrlOverride = 'SetWebViewUrlOverride',
    ClearWebViewUrlOverride = 'ClearWebViewUrlOverride',
    GetApplicationTimeInfo = 'GetApplicationTimeInfo',
    ForwardLogs = 'ForwardLogs',
    OpenStoreForUpdate = 'OpenStoreForUpdate',
    // Iap (Payment) contract methods — mirror plugins/src/commands/gfn/payment.json.
    GetStoreInfo = 'GetStoreInfo',
    GetProductsData = 'GetProductsData',
    PurchaseSubscription = 'PurchaseSubscription',
    GetPurchaseUpdates = 'GetPurchaseUpdates',
    RestorePurchases = 'RestorePurchases',
    AcknowledgeFulfillment = 'AcknowledgeFulfillment'
}

export interface VegaBridgeRequest {
    id?: string;
    category: VegaBridgeCategory;
    method: string;
    params: Record<string, unknown>;
}

export interface VegaBridgeError {
    code: string;
    message: string;
}

export interface VegaBridgeResponse {
    id?: string;
    handled: boolean;
    payload?: unknown;
    error?: VegaBridgeError;
}

export enum VegaBridgeErrorCode {
    InvalidJson = 'INVALID_JSON',
    InvalidRequest = 'INVALID_REQUEST',
    UnknownMethod = 'UNKNOWN_METHOD',
    HandlerFailed = 'HANDLER_FAILED',
    Timeout = 'TIMEOUT',
    Unavailable = 'UNAVAILABLE'
}

export interface VegaShellInfo {
    isVegaWebView: boolean;
    version: string;
}

/**
 * Generic transport envelope carried in both directions between the RN shell
 * and the WebView. Bridge commands/responses, log forwarding, telemetry, etc.
 * are all message types on this one transport. `data` is intentionally loose;
 * each message-type consumer validates/casts its own payload.
 */
export enum ShellMessageType {
    BridgeCommand = 'bridgeCommand',
    BridgeResponse = 'bridgeResponse',
    Telemetry = 'telemetry',
    Tracing = 'tracing',
    Exception = 'exception',
    Log = 'log',
    AppWindowState = 'appWindowState'
}

export interface ShellMessage {
    messageType: ShellMessageType;
    data: unknown;
}

/** Foreground/background state forwarded from the RN shell's AppState. */
export type ApplicationWindowState = 'foreground' | 'background';

/** Payload for {@link ShellMessageType.AppWindowState} messages. */
export interface AppWindowStatePayload {
    state: ApplicationWindowState;
}

export type ShellMessageHandler = (message: ShellMessage) => void;

export interface WindowWithVegaBridge extends Window {
    /** Web → shell channel, injected by the RN WebView framework. */
    ReactNativeWebView?: { postMessage(message: string): void };
    /** Shell context injected before content load (see buildShellInfoInjectScript). */
    vegaShellInfo?: VegaShellInfo;
    /** Shell → web transport entrypoint, installed by VegaHelperService. */
    onShellMessage?: ShellMessageHandler;
}
